/**
 * A QUOTE'S FOLLOW-UP, ON THE ENQUIRY'S PANEL (ops #86, `docs/plan-coach-review.md` section 5): the
 * steps the move to `quoted` wrote, each with its day, its due date and its state, and under each
 * what was recorded as it was done, the call or the email and what came of it. Read as the panel
 * opens, one small read; nothing here sends or edits anything. A step is done on the Tasks screen,
 * where its email is written out to copy into hello@.
 *
 * Drawn only once there is something to show: an enquiry never quoted has no steps.
 */
import { useEffect, useState } from 'react';
import { TASK_STATE_LABELS } from '@fk/shared';
import { ApiError, getFollowUps, type FollowUpStep, type FollowUpTouch } from '../../api.ts';
import { Pill } from '../../components/AppShell.tsx';
import { PanelRule } from '../../components/ListView.tsx';
import { mediumDay } from '../../format.ts';
import { TOUCH_OUTCOME_LABELS } from '../../labels.ts';

const DAY_WORDS: Readonly<Record<1 | 2 | 3, string>> = {
  1: 'Day one: a call and the day-one email',
  2: 'Day two: a call and the day-two email',
  3: 'Day three: the information sheet, then stop',
};

/** "Called: no answer", "Emailed: sent". */
export function touchWords(touch: FollowUpTouch): string {
  const how =
    touch.channel === 'phone' ? 'Called' : touch.channel === 'email' ? 'Emailed' : 'Reached';
  return `${how}: ${TOUCH_OUTCOME_LABELS[touch.outcome].toLowerCase()}`;
}

export function FollowUps({ enquiryId }: { enquiryId: string }) {
  const [steps, setSteps] = useState<readonly FollowUpStep[] | null>(null);
  const [touches, setTouches] = useState<readonly FollowUpTouch[]>([]);
  const [failed, setFailed] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    getFollowUps(enquiryId)
      .then((answer) => {
        if (!live) return;
        setSteps(answer.steps);
        setTouches(answer.touches);
      })
      .catch((thrown: unknown) => {
        if (live) {
          setFailed(thrown instanceof ApiError ? thrown.message : 'The follow-up did not load.');
        }
      });
    return () => {
      live = false;
    };
  }, [enquiryId]);

  if (failed) {
    return (
      <>
        <PanelRule />
        <p className="text-sm text-coral">The follow-up did not load: {failed}</p>
      </>
    );
  }
  if (steps === null || steps.length === 0) return null;
  const latest = steps[0]!.round;
  const shown = steps.filter((step) => step.round === latest);

  return (
    <>
      <PanelRule />
      <h3 className="font-serif text-lg leading-7">The quote's follow-up</h3>
      <p className="text-xs text-navy/65">
        Each step is done on the Tasks screen, where its email is written out to send from hello@ by
        hand. Nothing here is sent by ops, and there is no fourth step.
      </p>
      <ol className="mt-2 flex flex-col gap-2">
        {shown.map((step) => {
          // A done step's touches were written in its batch, at the moment it was done.
          const done = touches.filter(
            (touch) => touch.touchNumber === step.day && touch.occurredAt === step.completedAt,
          );
          return (
            <li key={step.id} className="text-sm">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium">{step.day ? DAY_WORDS[step.day] : step.title}</span>
                <Pill tone={step.state === 'open' ? 'gold' : 'quiet'}>
                  {TASK_STATE_LABELS[step.state]}
                </Pill>
              </div>
              {step.dueOn ? (
                <p className="text-xs text-navy/65">Due {mediumDay(step.dueOn)}</p>
              ) : null}
              {step.state === 'done' && done.length > 0 ? (
                <ul className="text-xs text-navy/75">
                  {done.map((touch) => (
                    <li key={touch.id}>{touchWords(touch)}</li>
                  ))}
                </ul>
              ) : null}
            </li>
          );
        })}
      </ol>
    </>
  );
}
